import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

const SUBSCRIPTION_DRAWER_SELECTOR = '[data-cart-subscription-plan-drawer]';
const DEFERRED_CART_DRAWER_FLAG = '__macraDeferredCartDrawerOpen';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #shouldDeferToSubscriptionDrawer(event) {
    const source = `${event?.detail?.data?.source || ''}`.trim();
    if (source !== 'product-form-component') return false;

    const target = event?.target instanceof Element ? event.target.closest('product-form-component') : null;
    if (!(target instanceof HTMLElement)) return false;
    if (!target.closest('.product-information .buy-buttons-block')) return false;

    const templateName = `${window.Theme?.template?.name || ''}`.trim().toLowerCase();
    const isProductTemplate =
      /^product([._/-].*)?$/.test(templateName) ||
      document.body?.classList.contains('template-product') ||
      document.body?.classList.contains('template--product');

    if (!isProductTemplate) return false;

    return document.querySelector(SUBSCRIPTION_DRAWER_SELECTOR) instanceof HTMLElement;
  }

  #handleCartAdd = (event) => {
    if (event?.detail?.data?.source === 'cart-subscription-plan-drawer') return;

    if (this.#shouldDeferToSubscriptionDrawer(event)) {
      window[DEFERRED_CART_DRAWER_FLAG] = true;
      return;
    }

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
